export interface BillionaireHistoryRow {
  captured_at: string;
  rank: number;
  net_worth_usd: number;
  daily_change_usd: number | null;
  daily_change_percent: number | null;
}

export interface BillionaireItem {
  id: number;
  slug: string;
  name: string;
  rank: number;
  net_worth_usd: number;
  prior_net_worth_usd: number | null;
  daily_change_usd: number | null;
  daily_change_percent: number | null;
  source_of_wealth: string;
  country: string;
  gender: string;
  birth_date: string | null;
  age?: number | null;
  about?: string;
  profile_metadata?: Record<string, unknown>;
  related_questions?: string[];
  profile_url: string;
  image_url: string;
  image_cached: boolean;
  last_seen_at: string;
  source: string;
  history?: BillionaireHistoryRow[];
}

export interface ForbesResult {
  title?: string;
  url?: string;
  description?: string;
}

export interface BillionairesResponse {
  kind?: "ranking" | "person";
  view?: "profile" | "net_worth";
  query?: string;
  count?: number;
  requested_limit?: number;
  items?: BillionaireItem[];
  person?: BillionaireItem;
  error?: string;
  code?: string;
  source?: { name?: string; url?: string };
  forbes_result?: ForbesResult;
}

export function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function formatWorth(value: number | null | undefined): string {
  const amount = Number(value || 0);
  if (amount >= 1_000_000_000_000) return `$${(amount / 1_000_000_000_000).toFixed(2).replace(/\.?0+$/, "")}T`;
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  return `$${Math.round(amount).toLocaleString()}`;
}

export function formatChange(person: BillionaireItem): string {
  if (person.daily_change_usd == null) return "Daily change unavailable";
  const value = Number(person.daily_change_usd);
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  const pct = person.daily_change_percent;
  const pctText = pct == null ? "" : ` (${pct > 0 ? "+" : pct < 0 ? "−" : ""}${Math.abs(pct).toFixed(2)}%)`;
  return `${sign}${formatWorth(Math.abs(value))}${pctText} today`;
}

export function updatedLabel(value: string): string {
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return "Updated recently";
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 2) return "Updated a minute ago";
  if (minutes < 60) return `Updated ${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? "Updated 1 hour ago" : `Updated ${hours} hours ago`;
}
